import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './Suppliers.css';
import CreateSupplierModal from './CreateSupplierModal';
import SupplierItemsModal from './SupplierItemsModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Pagination, PaginationItem } from '@mui/material';
import {
  FaCaretDown,
  FaDownload,
  FaSyncAlt,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSearch,
  FaBuilding,
  FaBoxes,
  FaCreditCard,
  FaPlus,
  FaChevronDown,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaFlag,
  FaClock
} from 'react-icons/fa';

/**
 * Suppliers Component (Outpatient / Enterprise Design System Aligned)
 * 
 * Re-engineered to match OP screens (AppointmentsListTable & PrescriptionsListTable):
 * 1. Top Breadcrumb Bar: Clean navigation with Refresh & Back buttons.
 * 2. Unified Master Card (.unified-master-card):
 *    - Master accent gradient bar on top.
 *    - Master Toolbar Header:
 *      - Fused Search Group (Dropdown selector: All, Name, Address, Phone, Email + Input).
 *      - Common Calendar Utility: DateRangePickerComponent from CommonComponents.
 *      - Action CTAs: "Download CSV" & "+ Create New Supplier".
 *    - Master Tabs & Status Chips Bar:
 *      - Tabs: "All Suppliers ({count})", "Active ({active})", "Inactive ({inactive})".
 *      - Status Chips: Total, Active, Inactive pill indicators.
 *    - Master Data Table (.master-data-table):
 *      - Sleek uppercase headers, interactive filter popovers.
 *      - Clicking any row opens the full Supplier Details modal.
 *      - Modern active/inactive toggle switch.
 *      - Row actions dropdown: Edit, Supplier Items, Payments, Delete.
 *    - Master Pagination Footer (.master-pagination-footer):
 *      - Material-UI <Pagination> with Previous & Next labels.
 *      - "Showing page X of Y (N suppliers)".
 * 
 * Includes comprehensive explanatory comments throughout.
 */
const Suppliers = () => {
  const navigate = useNavigate();

  // ─── Primary State Variables (Strictly Loaded via Backend API) ─────────────
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState(null);
  const [selectedSupplierForItems, setSelectedSupplierForItems] = useState(null);
  const [selectedSupplierForDetails, setSelectedSupplierForDetails] = useState(null);
  const [activeRowActionsId, setActiveRowActionsId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ─── Search State ──────────────────────────────────────────────────────────
  const [searchCategory, setSearchCategory] = useState('all'); // 'all' | 'name' | 'address' | 'phone' | 'email'
  const [searchQuery, setSearchQuery] = useState('');

  // ─── Master Tabs Bar State ──────────────────────────────────────────────────
  const [statusTab, setStatusTab] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'INACTIVE'

  // ─── Column-Specific Filter Popovers ────────────────────────────────────────
  const [filters, setFilters] = useState({
    name: '',
    address: '',
    phone1: '',
    phone2: '',
    email: '',
    status: 'ALL'
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const filterBarRef = useRef(null);

  // ─── Pagination States (Standard OP Pagination) ─────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  /**
   * Click-outside handler to close active filter popovers and row actions dropdowns
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterBarRef.current && !filterBarRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
      if (!event.target.closest('.supplier-row-actions-dropdown-wrapper')) {
        setActiveRowActionsId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  /**
   * Toggles dropdown open/close for a given filter header
   */
  const toggleDropdown = (filterName) => {
    setActiveDropdown((prev) => (prev === filterName ? null : filterName));
  };

  /**
   * Updates an individual column filter
   */
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  /**
   * Clears an individual column filter
   */
  const clearSingleFilter = (field) => {
    setFilters((prev) => ({
      ...prev,
      [field]: field === 'status' ? 'ALL' : ''
    }));
  };

  /**
   * Resets all filters and search query
   */
  const clearAllFilters = () => {
    setSearchQuery('');
    setFilters({
      name: '',
      address: '',
      phone1: '',
      phone2: '',
      email: '',
      status: 'ALL'
    });
    setStatusTab('ALL');
    setActiveDropdown(null);
    setCurrentPage(1);
  };

  // ─── Backend Integration ───────────────────────────────────────────────────
  const [backendCounts, setBackendCounts] = useState(null);

  const fetchCounts = async () => {
    try {
      const res = await axios.get(ENDPOINTS.GET_SUPPLIER_COUNTS);
      if (res.data) {
        setBackendCounts(res.data);
      }
    } catch (err) {
      // Gracefully silent if server is still spinning up
    }
  };

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(ENDPOINTS.GET_ALL_SUPPLIERS, {
        params: { page: 0, size: 500, sortBy: 'id', sortDirection: 'desc' }
      });
      if (res.data) {
        const list = Array.isArray(res.data) ? res.data : (res.data.content || []);
        setSuppliers(list);
      } else {
        setSuppliers([]);
      }
    } catch (err) {
      console.warn("Could not fetch suppliers from backend API:", err);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchCounts();
  }, []);

  /**
   * Toggles active/inactive status of a supplier
   */
  const handleToggleStatus = async (supplierId) => {
    // Optimistic UI state update
    setSuppliers((prev) =>
      prev.map((s) =>
        s.id === supplierId
          ? { ...s, status: s.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' }
          : s
      )
    );

    try {
      await axios.patch(ENDPOINTS.TOGGLE_SUPPLIER_STATUS(supplierId));
      fetchCounts();
    } catch (err) {
      console.warn("Could not sync toggle status to backend", err);
    }
  };

  /**
   * Deletes a supplier from the list
   */
  const handleDeleteSupplier = async (supplierId) => {
    if (window.confirm("Are you sure you want to delete this supplier?")) {
      setSuppliers((prev) => prev.filter((s) => s.id !== supplierId));
      try {
        await axios.delete(ENDPOINTS.DELETE_SUPPLIER(supplierId));
        fetchCounts();
      } catch (err) {
        console.warn("Could not delete supplier from backend", err);
      }
    }
  };

  /**
   * Handles saving a supplier from CreateSupplierModal
   */
  const handleSaveSupplier = async (savedSupplier) => {
    try {
      if (supplierToEdit) {
        const res = await axios.put(ENDPOINTS.UPDATE_SUPPLIER(savedSupplier.id), savedSupplier);
        const updated = res.data || savedSupplier;
        setSuppliers((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s))
        );
        setSupplierToEdit(null);
      } else {
        const res = await axios.post(ENDPOINTS.ADD_SUPPLIER, savedSupplier);
        const created = res.data || savedSupplier;
        setSuppliers((prev) => [created, ...prev]);
        setCurrentPage(1);
      }
      fetchCounts();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || "Failed to save supplier.";
      toast.error(`Save error: ${errorMsg}`);
    }
  };

  /**
   * Navigates to the dedicated pharmacySupplierPayment screen
   */
  const handleOpenPayments = (supplier) => {
    navigate('/pharmacy/pharmacySupplierPayment', {
      state: { supplier }
    });
  };

  /**
   * Opens the CreateSupplierModal in edit mode
   */
  const handleEditSupplier = (supplier) => {
    setSupplierToEdit(supplier);
    setIsCreateModalOpen(true);
  };

  /**
   * Refreshes the suppliers list with spin animation
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchSuppliers();
    fetchCounts();
    setTimeout(() => {
      setIsRefreshing(false);
    }, 600);
  };

  /**
   * Exports filtered suppliers into CSV
   */
  const handleDownloadCSV = () => {
    const headers = [
      'ID',
      'Name',
      'Description',
      'Address',
      'Phone1',
      'Phone2',
      'Email',
      'GST Number',
      'DL1 Number',
      'DL2 Number',
      'Status'
    ];
    const rows = filteredSuppliers.map((s) => [
      s.id,
      `"${s.name.replace(/"/g, '""')}"`,
      `"${(s.description || '').replace(/"/g, '""')}"`,
      `"${s.address.replace(/"/g, '""')}"`,
      `"${s.phone1}"`,
      `"${s.phone2 || ''}"`,
      `"${s.email || ''}"`,
      `"${s.gstNumber || ''}"`,
      `"${s.dl1Number || ''}"`,
      `"${s.dl2Number || ''}"`,
      s.status
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `pharmacy_suppliers_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── Filtered Suppliers Pipeline ───────────────────────────────────────────
  const filteredSuppliers = useMemo(() => {
    return suppliers.filter((s) => {
      // 1. Status Tab filter
      if (statusTab === 'ACTIVE' && s.status !== 'ACTIVE') return false;
      if (statusTab === 'INACTIVE' && s.status !== 'INACTIVE') return false;



      // 3. Fused Toolbar Search Input
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        if (searchCategory === 'name') {
          const match = (s.name || '').toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q);
          if (!match) return false;
        } else if (searchCategory === 'address') {
          if (!(s.address || '').toLowerCase().includes(q)) return false;
        } else if (searchCategory === 'phone') {
          const match = (s.phone1 || '').includes(q) || (s.phone2 || '').includes(q);
          if (!match) return false;
        } else if (searchCategory === 'email') {
          if (!(s.email || '').toLowerCase().includes(q)) return false;
        } else {
          const combined = `${s.name} ${s.description} ${s.address} ${s.phone1} ${s.phone2} ${s.email} ${s.gstNumber} ${s.dl1Number} ${s.dl2Number}`.toLowerCase();
          if (!combined.includes(q)) return false;
        }
      }

      // 4. Column Header Popover Filters
      if (filters.name && !(`${s.name} ${s.description}`.toLowerCase().includes(filters.name.toLowerCase()))) {
        return false;
      }
      if (filters.address && !(s.address || '').toLowerCase().includes(filters.address.toLowerCase())) {
        return false;
      }
      if (filters.phone1 && !(s.phone1 || '').includes(filters.phone1)) {
        return false;
      }
      if (filters.phone2 && !(s.phone2 || '').includes(filters.phone2)) {
        return false;
      }
      if (filters.email && !(s.email || '').toLowerCase().includes(filters.email.toLowerCase())) {
        return false;
      }
      if (filters.status !== 'ALL' && s.status !== filters.status) {
        return false;
      }

      return true;
    });
  }, [suppliers, statusTab, searchQuery, searchCategory, filters]);

  // Counts for tabs and status chips (synchronized with backend metrics)
  const totalCount = backendCounts ? backendCounts.total : suppliers.length;
  const activeCount = useMemo(
    () => (backendCounts ? backendCounts.active : suppliers.filter((s) => s.status === 'ACTIVE').length),
    [suppliers, backendCounts]
  );
  const inactiveCount = useMemo(
    () => (backendCounts ? backendCounts.inactive : suppliers.filter((s) => s.status === 'INACTIVE').length),
    [suppliers, backendCounts]
  );

  // Pagination calculation
  const totalPages = Math.ceil(filteredSuppliers.length / rowsPerPage) || 1;
  const paginatedSuppliers = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredSuppliers.slice(start, start + rowsPerPage);
  }, [filteredSuppliers, currentPage, rowsPerPage]);

  const hasActiveFilters =
    searchQuery ||
    filters.name ||
    filters.address ||
    filters.phone1 ||
    filters.phone2 ||
    filters.email ||
    filters.status !== 'ALL';

  return (
    <div className="suppliers-page-wrapper">
      {/* ─── Master Unified Enterprise Card (OP Screen Architecture) ───── */}
      <div className="unified-master-card">
        {/* Top Accent Bar */}
        <div className="master-card-accent-bar" />

        {/* Master Toolbar Header: Fused Search on left, Actions on right */}
        <div className="master-toolbar-header">
          <div className="toolbar-search-box">
            <div className="fused-search-group">
              <select
                value={searchCategory}
                onChange={(e) => {
                  setSearchCategory(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Fields</option>
                <option value="name">Supplier Name</option>
                <option value="address">Address / City</option>
                <option value="phone">Phone Number</option>
                <option value="email">Email Address</option>
              </select>

              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder={
                    searchCategory === 'name'
                      ? 'Search by supplier name...'
                      : searchCategory === 'address'
                      ? 'Search by city or address...'
                      : searchCategory === 'phone'
                      ? 'Search by phone number...'
                      : searchCategory === 'email'
                      ? 'Search by email address...'
                      : 'Search suppliers...'
                  }
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Action CTAs: Refresh, Download CSV & Create New Supplier */}
          <div className="toolbar-right-group">
            <button
              type="button"
              className="master-secondary-btn"
              onClick={handleRefresh}
              title="Refresh suppliers list"
              aria-label="Refresh"
            >
              <FaSyncAlt className={isRefreshing ? 'suppliers-spin' : ''} size={13} /> Refresh
            </button>
            <button
              type="button"
              className="master-secondary-btn"
              onClick={handleDownloadCSV}
              title="Download suppliers data in CSV format"
            >
              <FaDownload size={13} /> Download
            </button>
            <button
              type="button"
              className="master-new-appt-btn"
              onClick={() => {
                setSupplierToEdit(null);
                setIsCreateModalOpen(true);
              }}
              title="Create a new medicine supplier"
            >
              <FaPlus size={13} /> Create New Supplier
            </button>
          </div>
        </div>

        {/* Master Tabs Bar: Status Tabs matching IP style with Green / Red accents */}
        <div className="master-tabs-bar">
          <div className="tabs-list">
            <button
              type="button"
              className={`tab-btn tab-all ${statusTab === 'ALL' ? 'active-tab-btn active-tab-all' : ''}`}
              onClick={() => {
                setStatusTab('ALL');
                setCurrentPage(1);
              }}
            >
              All Suppliers ({totalCount})
            </button>
            <button
              type="button"
              className={`tab-btn tab-active ${statusTab === 'ACTIVE' ? 'active-tab-btn active-tab-active' : ''}`}
              onClick={() => {
                setStatusTab('ACTIVE');
                setCurrentPage(1);
              }}
            >
              Active ({activeCount})
            </button>
            <button
              type="button"
              className={`tab-btn tab-inactive ${statusTab === 'INACTIVE' ? 'active-tab-btn active-tab-inactive' : ''}`}
              onClick={() => {
                setStatusTab('INACTIVE');
                setCurrentPage(1);
              }}
            >
              Inactive ({inactiveCount})
            </button>
          </div>
        </div>

        {/* Active Filter Chips Banner */}
        {hasActiveFilters && (
          <div className="suppliers-active-filters-chips">
            <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
              Active Filters:
            </span>
            {searchQuery && (
              <span className="suppliers-filter-chip">
                Search: "{searchQuery}"
                <span className="suppliers-filter-chip-remove" onClick={() => setSearchQuery('')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.name && (
              <span className="suppliers-filter-chip">
                Name: {filters.name}
                <span className="suppliers-filter-chip-remove" onClick={() => clearSingleFilter('name')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.address && (
              <span className="suppliers-filter-chip">
                Address: {filters.address}
                <span className="suppliers-filter-chip-remove" onClick={() => clearSingleFilter('address')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.phone1 && (
              <span className="suppliers-filter-chip">
                Phone 1: {filters.phone1}
                <span className="suppliers-filter-chip-remove" onClick={() => clearSingleFilter('phone1')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.phone2 && (
              <span className="suppliers-filter-chip">
                Phone 2: {filters.phone2}
                <span className="suppliers-filter-chip-remove" onClick={() => clearSingleFilter('phone2')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.email && (
              <span className="suppliers-filter-chip">
                Email: {filters.email}
                <span className="suppliers-filter-chip-remove" onClick={() => clearSingleFilter('email')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.status !== 'ALL' && (
              <span className="suppliers-filter-chip">
                Status: {filters.status}
                <span className="suppliers-filter-chip-remove" onClick={() => clearSingleFilter('status')}>
                  <FaTimes />
                </span>
              </span>
            )}
            <span className="suppliers-clear-all-link" onClick={clearAllFilters}>
              Clear All
            </span>
          </div>
        )}

        {/* Master Data Table Container */}
        <div className="master-table-container">
          <table className="master-data-table pe-table">
            <thead ref={filterBarRef} className={activeDropdown ? 'has-open-dropdown' : ''}>
              <tr>
                {/* 1. Name */}
                <th
                  className={activeDropdown === 'name' ? 'th-popover-open' : ''}
                  style={{ minWidth: '220px', width: '22%', position: 'relative', zIndex: activeDropdown === 'name' ? 999999 : 1 }}
                >
                  <div
                    className={`th-filter-trigger ${filters.name ? 'active' : ''}`}
                    onClick={() => toggleDropdown('name')}
                    title="Filter by Name"
                  >
                    <div className="th-filter-title">
                      <FaBuilding className="th-icon" />
                      <span>Name</span>
                      <FaChevronDown className="th-caret" />
                    </div>
                  </div>

                    {activeDropdown === 'name' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Supplier Name</label>
                        <input
                          type="text"
                          className="header-filter-input"
                          placeholder="Search name or contact person..."
                          value={filters.name}
                          onChange={(e) => handleFilterChange('name', e.target.value)}
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => clearSingleFilter('name')}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActiveDropdown(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 2. Address */}
                  <th
                    className={activeDropdown === 'address' ? 'th-popover-open' : ''}
                    style={{ minWidth: '200px', width: '20%', position: 'relative', zIndex: activeDropdown === 'address' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${filters.address ? 'active' : ''}`}
                      onClick={() => toggleDropdown('address')}
                      title="Filter by Address"
                    >
                      <div className="th-filter-title">
                        <FaMapMarkerAlt className="th-icon" />
                        <span>Address</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>

                    {activeDropdown === 'address' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Address / City</label>
                        <input
                          type="text"
                          className="header-filter-input"
                          placeholder="Search city, state, street..."
                          value={filters.address}
                          onChange={(e) => handleFilterChange('address', e.target.value)}
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => clearSingleFilter('address')}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActiveDropdown(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 3. Phone 1 */}
                  <th
                    className={activeDropdown === 'phone1' ? 'th-popover-open' : ''}
                    style={{ minWidth: '130px', width: '12%', position: 'relative', zIndex: activeDropdown === 'phone1' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${filters.phone1 ? 'active' : ''}`}
                      onClick={() => toggleDropdown('phone1')}
                      title="Filter by Phone 1"
                    >
                      <div className="th-filter-title">
                        <FaPhone className="th-icon" />
                        <span>Phone 1</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>

                    {activeDropdown === 'phone1' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Phone 1</label>
                        <input
                          type="text"
                          className="header-filter-input"
                          placeholder="Search primary phone..."
                          value={filters.phone1}
                          onChange={(e) => handleFilterChange('phone1', e.target.value)}
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => clearSingleFilter('phone1')}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActiveDropdown(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 4. Phone 2 */}
                  <th
                    className={activeDropdown === 'phone2' ? 'th-popover-open' : ''}
                    style={{ minWidth: '130px', width: '12%', position: 'relative', zIndex: activeDropdown === 'phone2' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${filters.phone2 ? 'active' : ''}`}
                      onClick={() => toggleDropdown('phone2')}
                      title="Filter by Phone 2"
                    >
                      <div className="th-filter-title">
                        <FaPhone className="th-icon" />
                        <span>Phone 2</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>

                    {activeDropdown === 'phone2' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Phone 2</label>
                        <input
                          type="text"
                          className="header-filter-input"
                          placeholder="Search secondary phone..."
                          value={filters.phone2}
                          onChange={(e) => handleFilterChange('phone2', e.target.value)}
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => clearSingleFilter('phone2')}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActiveDropdown(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 5. Email */}
                  <th
                    className={activeDropdown === 'email' ? 'th-popover-open' : ''}
                    style={{ minWidth: '180px', width: '14%', position: 'relative', zIndex: activeDropdown === 'email' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${filters.email ? 'active' : ''}`}
                      onClick={() => toggleDropdown('email')}
                      title="Filter by Email"
                    >
                      <div className="th-filter-title">
                        <FaEnvelope className="th-icon" />
                        <span>Email</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>

                    {activeDropdown === 'email' && (
                      <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Email</label>
                        <input
                          type="text"
                          className="header-filter-input"
                          placeholder="Search email address..."
                          value={filters.email}
                          onChange={(e) => handleFilterChange('email', e.target.value)}
                          autoFocus
                        />
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => clearSingleFilter('email')}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActiveDropdown(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 6. Status */}
                  <th
                    className={activeDropdown === 'status' ? 'th-popover-open' : ''}
                    style={{ minWidth: '110px', width: '10%', textAlign: 'center', position: 'relative', zIndex: activeDropdown === 'status' ? 999999 : 1 }}
                  >
                    <div
                      className={`th-filter-trigger ${filters.status !== 'ALL' ? 'active' : ''}`}
                      onClick={() => toggleDropdown('status')}
                      title="Filter by Status"
                    >
                      <div className="th-filter-title">
                        <FaFlag className="th-icon" />
                        <span>Status</span>
                        <FaChevronDown className="th-caret" />
                      </div>
                    </div>

                    {activeDropdown === 'status' && (
                      <div className="table-header-popover" style={{ minWidth: '190px' }} onClick={(e) => e.stopPropagation()}>
                        <label className="popover-title">Filter by Status</label>
                        <select
                          value={filters.status}
                          onChange={(e) => handleFilterChange('status', e.target.value)}
                          className="header-filter-select"
                          autoFocus
                        >
                          <option value="ALL">All Statuses</option>
                          <option value="ACTIVE">Active Only</option>
                          <option value="INACTIVE">Inactive Only</option>
                        </select>
                        <div className="popover-btn-group">
                          <button
                            type="button"
                            className="pop-btn-clear"
                            onClick={() => {
                              clearSingleFilter('status');
                              setActiveDropdown(null);
                            }}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            className="pop-btn-apply"
                            onClick={() => setActiveDropdown(null)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 7. Actions */}
                  <th style={{ minWidth: '110px', width: '10%', textAlign: 'center' }}>
                    <div className="th-filter-trigger static">
                      <div className="th-filter-title">
                        <span>Actions</span>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="table-state-cell">
                      <div className="table-loading-state">
                        <FaClock size={32} color="#64748b" />
                        <p>Loading Suppliers...</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedSuppliers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="table-state-cell">
                      <div className="table-empty-state">
                        <FaSearch size={32} color="#94a3b8" />
                        <h4>No Suppliers Found</h4>
                        <p>No records match your selected criteria. Try adjusting the search query or create a new supplier.</p>
                        <button
                          type="button"
                          className="master-new-appt-btn"
                          style={{ marginTop: 12 }}
                          onClick={() => {
                            setSupplierToEdit(null);
                            setIsCreateModalOpen(true);
                          }}
                        >
                          <FaPlus size={12} /> Create New Supplier
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedSuppliers.map((supplier, index) => {
                    const isNearBottom = paginatedSuppliers.length > 4 && index >= paginatedSuppliers.length - 2;
                    const isRowActionOpen = activeRowActionsId === supplier.id;
                    return (
                      <tr
                        key={supplier.id}
                        className={`table-data-row ${isRowActionOpen ? 'row-actions-active' : ''}`}
                        onClick={() => setSelectedSupplierForDetails(supplier)}
                        title="Click row to view supplier details"
                        style={isRowActionOpen ? { position: 'relative', zIndex: 100 } : undefined}
                      >
                        {/* Supplier Name & Description */}
                        <td>
                          <div className="supplier-name-cell" title={`${supplier.name}${supplier.description ? ' - ' + supplier.description : ''}`}>
                            <span className="supplier-main-name">{supplier.name}</span>
                            {supplier.description && (
                              <span className="supplier-contact-sub">{supplier.description}</span>
                            )}
                          </div>
                        </td>

                        {/* Address / City */}
                        <td>
                          <span className="pe-supplier-address" title={supplier.address}>
                            {supplier.address || '—'}
                          </span>
                        </td>

                        {/* Phone 1 */}
                        <td>
                          <span className="pe-mono-chip phone">{supplier.phone1 || '—'}</span>
                        </td>

                        {/* Phone 2 */}
                        <td>
                          <span className="pe-mono-chip phone">{supplier.phone2 || '—'}</span>
                        </td>

                        {/* Email */}
                        <td>
                          <span className="pe-supplier-email" title={supplier.email || ''}>
                            {supplier.email || '—'}
                          </span>
                        </td>

                        {/* Status Toggle */}
                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <label
                            className="supplier-status-toggle"
                            title={`Click to ${supplier.status === 'ACTIVE' ? 'deactivate' : 'activate'} this supplier`}
                          >
                            <input
                              type="checkbox"
                              checked={supplier.status === 'ACTIVE'}
                              onChange={() => handleToggleStatus(supplier.id)}
                            />
                            <span className="supplier-toggle-slider" />
                          </label>
                        </td>

                        {/* Row Actions Menu */}
                        <td
                          style={{ textAlign: 'center', overflow: 'visible', position: 'relative', zIndex: isRowActionOpen ? 100 : 'auto' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="supplier-row-actions-dropdown-wrapper">
                            <button
                              type="button"
                              className={`supplier-row-actions-trigger ${isRowActionOpen ? 'active' : ''}`}
                              onClick={() =>
                                setActiveRowActionsId(activeRowActionsId === supplier.id ? null : supplier.id)
                              }
                              title="Actions menu"
                            >
                              Actions <FaChevronDown className="actions-caret" />
                            </button>

                            {isRowActionOpen && (
                              <div className={`supplier-row-actions-menu ${isNearBottom ? 'open-upwards' : ''}`}>
                                {/* 1. Edit */}
                                <button
                                  type="button"
                                  className="supplier-row-action-item"
                                  onClick={() => {
                                    handleEditSupplier(supplier);
                                    setActiveRowActionsId(null);
                                  }}
                                >
                                  <FaEdit className="action-menu-icon" />
                                  <span>Edit Details</span>
                                </button>

                                {/* 2. Supplier Items */}
                                <button
                                  type="button"
                                  className="supplier-row-action-item"
                                  onClick={() => {
                                    setSelectedSupplierForItems(supplier);
                                    setActiveRowActionsId(null);
                                  }}
                                >
                                  <FaBoxes className="action-menu-icon icon-blue" />
                                  <span>Supplier Items</span>
                                </button>

                                {/* 3. Payments */}
                                <button
                                  type="button"
                                  className="supplier-row-action-item"
                                  onClick={() => {
                                    handleOpenPayments(supplier);
                                    setActiveRowActionsId(null);
                                  }}
                                >
                                  <FaCreditCard className="action-menu-icon icon-green" />
                                  <span>Payments</span>
                                </button>

                                {/* 4. Delete */}
                                <div className="supplier-menu-divider" />
                                <button
                                  type="button"
                                  className="supplier-row-action-item delete-item"
                                  onClick={() => {
                                    handleDeleteSupplier(supplier.id);
                                    setActiveRowActionsId(null);
                                  }}
                                >
                                  <FaTrash className="action-menu-icon" />
                                  <span>Delete</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
        </div>

        {/* Master Pagination Footer (Standard OP Material-UI Pagination) */}
        <div className="master-pagination-footer">
          <span className="pagination-summary-text">
            Showing page <strong>{currentPage}</strong> of <strong>{totalPages || 1}</strong> ({filteredSuppliers.length} suppliers)
          </span>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                components={{
                  previous: () => <span>Previous</span>,
                  next: () => <span>Next</span>,
                }}
              />
            )}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </div>
      </div>

      {/* ─── 3. Modal Dialogs ──────────────────────────────────────────────── */}
      {/* Create / Edit Supplier Modal */}
      <CreateSupplierModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSupplierToEdit(null);
        }}
        onSave={handleSaveSupplier}
        supplierToEdit={supplierToEdit}
      />

      {/* Supplier Items Catalog Modal */}
      <SupplierItemsModal
        isOpen={Boolean(selectedSupplierForItems)}
        onClose={() => setSelectedSupplierForItems(null)}
        supplier={selectedSupplierForItems}
      />

      {/* Supplier Details View Modal (Triggered by clicking row) */}
      {selectedSupplierForDetails && (
        <div
          className="supplier-modal-overlay"
          onClick={() => setSelectedSupplierForDetails(null)}
        >
          <div
            className="supplier-modal-container details-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="supplier-modal-header">
              <div className="supplier-modal-title-group">
                <div className="supplier-modal-icon-badge">
                  <FaBuilding />
                </div>
                <div>
                  <h3 className="supplier-modal-title">Supplier Details</h3>
                  <p className="supplier-modal-subtitle">
                    ID #{selectedSupplierForDetails.id} &bull; {selectedSupplierForDetails.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="supplier-modal-close-btn"
                onClick={() => setSelectedSupplierForDetails(null)}
                aria-label="Close modal"
              >
                <FaTimes />
              </button>
            </div>

            <div className="supplier-modal-body">
              <div className="supplier-details-grid">
                <div className="supplier-detail-field full-width">
                  <span className="detail-label">Supplier Name</span>
                  <span className="detail-value bold-value">{selectedSupplierForDetails.name}</span>
                </div>

                {selectedSupplierForDetails.description && (
                  <div className="supplier-detail-field full-width">
                    <span className="detail-label">Description / Contact Person</span>
                    <span className="detail-value">{selectedSupplierForDetails.description}</span>
                  </div>
                )}

                <div className="supplier-detail-field full-width">
                  <span className="detail-label">Address</span>
                  <span className="detail-value">{selectedSupplierForDetails.address}</span>
                </div>

                <div className="supplier-detail-field">
                  <span className="detail-label">Phone 1</span>
                  <span className="detail-value">{selectedSupplierForDetails.phone1}</span>
                </div>

                <div className="supplier-detail-field">
                  <span className="detail-label">Phone 2</span>
                  <span className="detail-value">{selectedSupplierForDetails.phone2 || 'Not Provided'}</span>
                </div>

                <div className="supplier-detail-field">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{selectedSupplierForDetails.email || 'Not Provided'}</span>
                </div>

                <div className="supplier-detail-field">
                  <span className="detail-label">Status</span>
                  <span
                    className={`detail-badge ${
                      selectedSupplierForDetails.status === 'ACTIVE' ? 'badge-active' : 'badge-inactive'
                    }`}
                  >
                    {selectedSupplierForDetails.status}
                  </span>
                </div>

                <div className="supplier-detail-field">
                  <span className="detail-label">GST Number</span>
                  <span className="detail-value font-mono">
                    {selectedSupplierForDetails.gstNumber || 'N/A'}
                  </span>
                </div>

                <div className="supplier-detail-field">
                  <span className="detail-label">DL 1 Number (Drug License)</span>
                  <span className="detail-value font-mono">
                    {selectedSupplierForDetails.dl1Number || 'N/A'}
                  </span>
                </div>

                <div className="supplier-detail-field">
                  <span className="detail-label">DL 2 Number (Drug License)</span>
                  <span className="detail-value font-mono">
                    {selectedSupplierForDetails.dl2Number || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            <div className="supplier-modal-footer">
              <button
                type="button"
                className="master-secondary-btn"
                onClick={() => setSelectedSupplierForDetails(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="master-new-appt-btn"
                onClick={() => {
                  const s = selectedSupplierForDetails;
                  setSelectedSupplierForDetails(null);
                  handleEditSupplier(s);
                }}
              >
                <FaEdit /> Edit Supplier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;
